import { AnimatePresence, motion } from "framer-motion";
import { LayoutGrid, X } from "lucide-react";
import { FavouriteMark, StoryMark } from "../FavouriteMark";
import { FlipbookLink } from "../FlipbookLink";

const Key = ({ children }) => (
  <span className="rounded border border-[#B39DDB]/70 bg-[#F0E8FA] px-1.5 py-0.5 font-mono text-[10px] text-[#2D1E3E]">{children}</span>
);

export function ControlsHint({ touch, onDismiss }) {
  return (
    <motion.div
      data-testid="controls-hint"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 16, transition: { duration: 0.12, delay: 0 } }}
      transition={{ duration: 0.6, delay: 0.4 }}
      style={{ x: "-50%" }}
      className={`fixed left-1/2 z-40 flex max-w-[92vw] items-center gap-4 rounded-full border border-[#7E57C2]/25 bg-white/85 px-5 py-3 text-xs tracking-wide text-[#4A3660] shadow-2xl backdrop-blur-xl ${
        touch ? "brand-gap-hint top-[10.5rem] w-[76vw] text-center" : "bottom-24"
      }`}
    >
      {touch ? (
        <span>Tap an archway to enter · ‹ › or swipe to move between paintings · tap a painting to step inside</span>
      ) : (
        <span className="flex flex-wrap items-center gap-2">
          Click an archway to enter
          <span className="text-[#B39DDB]">·</span> ‹ › or <Key>←</Key><Key>→</Key> to move between paintings
          <span className="text-[#B39DDB]">·</span> click a painting to step inside
        </span>
      )}
      <button
        data-testid="controls-hint-dismiss"
        onClick={onDismiss}
        aria-label="Dismiss controls hint"
        className="rounded-full p-1 text-[#7C6992] transition-colors hover:text-[#7E57C2]"
      >
        <X size={14} />
      </button>
    </motion.div>
  );
}

export function FocusLabel({ painting, touch, firstHint }) {
  return (
    <>
      <AnimatePresence>
        {painting && firstHint && (
          <motion.p
            key="first-hint"
            data-testid="doorway-first-hint"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, transition: { duration: 1.2 } }}
            transition={{ duration: 0.9, delay: 0.3 }}
            style={{ x: "-50%" }}
            className={`pointer-events-none fixed left-1/2 z-30 whitespace-nowrap rounded-full bg-white/75 px-4 py-0.5 font-hand text-2xl text-[#4A2D55] shadow-[0_8px_24px_-12px_rgba(91,58,133,0.45)] backdrop-blur-sm ${touch ? "top-[52%]" : "top-[69%]"}`}
          >
            {touch ? "tap to step inside" : "click to step inside"}
          </motion.p>
        )}
      </AnimatePresence>
      <AnimatePresence>
      {painting && (
        <motion.div
          key={painting.id}
          data-testid="painting-hover-label"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 6 }}
          transition={{ duration: 0.35 }}
          style={{ x: "-50%" }}
          className={`pointer-events-none fixed left-1/2 z-30 whitespace-nowrap rounded-xl border border-[#7E57C2]/30 bg-white/85 px-6 py-3 text-center shadow-2xl backdrop-blur-md ${touch ? "bottom-[calc(env(safe-area-inset-bottom)+5.25rem)]" : "top-[76%]"}`}
        >
          <p data-testid="hover-label-emotion" className="text-[10px] uppercase tracking-[0.35em] text-[#7E57C2]">{painting.emotion}</p>
          <p data-testid="hover-label-title" className="font-display text-2xl italic text-[#2D1E3E]">{painting.title}</p>
          <FavouriteMark painting={painting} testid="hover-label-favourite" />
          <StoryMark painting={painting} testid="hover-label-story" className="ml-2" />
          <p className="mt-0.5 text-[10px] tracking-wider text-[#7C6992]">{touch ? "tap to step inside" : "click to step inside"}</p>
        </motion.div>
      )}
      </AnimatePresence>
    </>
  );
}

// beside the Memory Wall: the open book's name, itself a link to the Flipbook (new tab)
export function FlipbookLabel({ show, touch }) {
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="hall-flipbook-label"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 6 }}
          transition={{ duration: 0.5 }}
          style={{ x: "-50%" }}
          className={`fixed bottom-[calc(env(safe-area-inset-bottom)+6rem)] [.walkpad_&]:bottom-[calc(env(safe-area-inset-bottom)+11.25rem)] left-1/2 z-[46] whitespace-nowrap rounded-full border border-[#FFC98A]/70 bg-[#FFF8EE]/90 shadow-[0_14px_30px_-16px_rgba(120,80,40,0.6)] backdrop-blur-md`}
        >
          <FlipbookLink testid="hall-flipbook-link" className="block px-6 py-2.5 font-display text-lg italic text-[#4A2D55] transition-colors duration-200 hover:text-[#7E57C2]">
            Open The Flipbook <span className="not-italic text-[#C27BA0]">›</span>
          </FlipbookLink>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function BenchLabel({ show, touch }) {
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="bench-label"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 6 }}
          transition={{ duration: 0.5 }}
          style={{ x: "-50%" }}
          className={`pointer-events-none fixed left-1/2 z-30 whitespace-nowrap rounded-xl border border-[#F8BBD9]/60 bg-white/85 px-6 py-3 text-center shadow-2xl backdrop-blur-md ${touch ? "top-[60%]" : "top-[76%]"}`}
        >
          <p className="text-[10px] uppercase tracking-[0.35em] text-[#C2185B]/80">The memory wall</p>
          <p className="font-display text-xl italic text-[#2D1E3E]">Take a seat on the bench.</p>
          <p className="mt-0.5 font-display text-sm italic text-[#7C6992]">The memories begin in a moment.</p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function TopBar({ onSwitch2D }) {
  return (
    <div className="pointer-events-none fixed left-6 right-24 top-6 z-40 flex items-center justify-between">
      <span />
      <button
        data-testid="webgl-fallback-toggle"
        onClick={onSwitch2D}
        aria-label="Switch to 2D view"
        title="2D view"
        className="pointer-events-auto hidden rounded-full border border-[#B39DDB]/60 bg-[#FBF7F2]/95 p-3 text-[#2D1E3E] backdrop-blur-xl transition-colors hover:border-[#7E57C2] hover:text-[#7E57C2] sm:block"
      >
        <LayoutGrid size={16} />
      </button>
    </div>
  );
}

export function Loader() {
  return (
    <motion.div
      data-testid="gallery-loader"
      exit={{ opacity: 0 }}
      transition={{ duration: 1 }}
      className="fixed inset-0 z-[45] flex flex-col items-center justify-center bg-[#F3E8FF]"
    >
    </motion.div>
  );
}

export function Reticle({ active }) {
  const s = active ? "h-6 w-6" : "h-3 w-3";
  const c = active ? "bg-[#7E57C2]" : "bg-[#7E57C2]/70";
  return (
    <div data-testid="gallery-reticle" className={`pointer-events-none fixed left-1/2 top-1/2 z-30 -translate-x-1/2 -translate-y-1/2 transition-[width,height] duration-300 ${s}`}>
      <span className={`absolute left-1/2 top-0 h-full w-px -translate-x-1/2 ${c}`} />
      <span className={`absolute left-0 top-1/2 h-px w-full -translate-y-1/2 ${c}`} />
    </div>
  );
}
